import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  ListTree,
  Info,
  CheckCircle,
  XCircle,
} from "lucide-react";

const BalancedParenthesesVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Example 1 (True)",
      s: "()[]{}",
    },
    {
      id: 2,
      name: "Example 2 (False)",
      s: "(]",
    },
    {
      id: 3,
      name: "Nested (True)",
      s: "([{}])",
    },
    {
      id: 4,
      name: "Mismatched (False)",
      s: "{[()]}()",
    },
    {
      id: 5,
      name: "Open Brackets Left (False)",
      s: "((",
    },
    {
      id: 6,
      name: "Closing First (False)",
      s: "){",
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [s, setS] = useState(defaultTestCases[0].s);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function isValid(s):", indent: 0 },
    { line: "stack = []", indent: 1 },
    { line: "map = { '(':')', '{':'}', '[':']' }", indent: 1 },
    { line: "", indent: 0 },
    { line: "for char in s:", indent: 1 },
    { line: "if char is an open bracket:", indent: 2 },
    { line: "stack.push(char)", indent: 3 },
    { line: "else: // char is a close bracket", indent: 2 },
    { line: "if stack is empty: return false", indent: 3 },
    { line: "lastOpen = stack.pop()", indent: 3 },
    { line: "if map[lastOpen] != char: return false", indent: 3 },
    { line: "", indent: 0 },
    { line: "return stack is empty", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (s) => {
    const allSteps = [];
    let stepCount = 0;
    const stack = [];
    const map = { "(": ")", "{": "}", "[": "]" };
    let result = null;

    const addStep = (description, highlightLine, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description,
        highlightLine,
        stack: [...stack],
        result,
        ...details,
      });
    };

    addStep("Start: Initialize an empty stack.", 1);

    for (let i = 0; i < s.length; i++) {
      const char = s[i];
      addStep(`Processing character at index ${i}: '${char}'.`, 4, {
        currentIndex: i,
      });

      if (map[char]) {
        addStep(`'${char}' is an open bracket. Push to stack.`, 5, {
          currentIndex: i,
        });
        stack.push(char);
        addStep(`Stack after push: [${stack.join(", ")}]`, 6, {
          currentIndex: i,
        });
      } else {
        if (stack.length === 0) {
          addStep(
            "Stack is empty, but found a closing bracket. Unbalanced.",
            8,
            { currentIndex: i, isFinal: true, result: false }
          );
          return allSteps;
        }

        const lastOpen = stack.pop();
        addStep(
          `'${char}' is a closing bracket. Pop from stack: '${lastOpen}'.`,
          9,
          { currentIndex: i, popped: lastOpen }
        );

        if (map[lastOpen] !== char) {
          addStep(
            `Mismatched brackets: '${lastOpen}' and '${char}'. Unbalanced.`,
            10,
            { currentIndex: i, isFinal: true, result: false }
          );
          return allSteps;
        }
        addStep(`Brackets '${lastOpen}' and '${char}' match. Continue.`, 10, {
          currentIndex: i,
        });
      }
    }

    if (stack.length === 0) {
      addStep("End of string. Stack is empty. Balanced.", 12, {
        isFinal: true,
        result: true,
      });
    } else {
      addStep(
        `End of string. Stack is not empty: [${stack.join(", ")}]. Unbalanced.`,
        12,
        { isFinal: true, result: false }
      );
    }

    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(s);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [s]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setS(testCases[index].s);
  };

  const addCustomTestCase = () => {
    try {
      const newTestCase = { id: Date.now(), name: `Custom`, s: customInput };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid input: ${error.message}`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <ListTree className="text-blue-500" /> Check for Balanced Parentheses
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given a string `s` containing just the characters '(', ')', '
                {", "}', '[' and ']', determine if the input string is valid. An
                input string is valid if open brackets are closed by the same
                type of brackets and in the correct order.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75 font-mono">"{tc.s}"</div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Custom: ([{}])"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4">
              <p className="text-sm font-semibold mb-2">Input String:</p>
              <div className="flex flex-wrap gap-1">
                {(s || "").split("").map((char, idx) => (
                  <div
                    key={idx}
                    className={`w-10 h-10 flex items-center justify-center border-2 rounded font-mono text-xl font-bold transition-all duration-300 ${
                      currentStepData?.currentIndex === idx
                        ? "bg-blue-200 border-blue-400 scale-110"
                        : "bg-gray-100 border-gray-300"
                    }`}
                  >
                    {char}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700">Stack</h4>
              <div className="p-2 bg-gray-50 rounded mt-1 flex flex-row-reverse items-center justify-end gap-2 min-h-[56px]">
                {currentStepData &&
                  currentStepData.stack.map((char, idx) => (
                    <div
                      key={idx}
                      className={`w-12 h-12 flex items-center justify-center rounded font-mono text-xl font-bold transition-all duration-300 ${
                        idx === currentStepData.stack.length - 1 &&
                        currentStepData.highlightLine === 9
                          ? "bg-yellow-200"
                          : "bg-gray-200"
                      }`}
                    >
                      {char}
                    </div>
                  ))}
              </div>
            </div>

            {currentStepData?.isFinal && (
              <div
                className={`mt-4 p-3 rounded-lg text-center ${
                  currentStepData.result ? "bg-green-100" : "bg-red-100"
                }`}
              >
                <div
                  className={`flex items-center justify-center font-bold ${
                    currentStepData.result ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {currentStepData.result ? (
                    <CheckCircle className="mr-2" />
                  ) : (
                    <XCircle className="mr-2" />
                  )}
                  Result: {currentStepData.result ? "Balanced" : "Not Balanced"}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Stack-Based Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The classic solution for this problem uses a stack. We iterate
              through the string: if we find an opening bracket, we push it onto
              the stack. If we find a closing bracket, we check the top of the
              stack. If the stack is empty or the top element is not the
              corresponding opening bracket, the string is invalid. After the
              loop, the string is only valid if the stack is empty.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(N)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N is the length of the string.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  A stack is the perfect data structure because of its Last-In,
                  First-Out (LIFO) nature, which matches how nested brackets
                  must be closed.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The final check is crucial: if the stack is not empty at the
                  end, it means there were unclosed opening brackets.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  A hash map is useful for easily matching closing brackets to
                  their opening counterparts.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-blue-200 border-2 border-blue-400"></div>
                <span>Current Character</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center font-bold">
                  (
                </div>
                <span>Item on Stack</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-yellow-200"></div>
                <span>Popped from Stack</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalancedParenthesesVisualizer;
